import Avatar from "@mui/material/Avatar";
import Box from "@mui/material/Box";
import Paper from "@mui/material/Paper";
import Stack from "@mui/material/Stack";
import Typography from "@mui/material/Typography";

import type { BluffRoster } from "./answerBluffPresence";
import { bluffAvatarEmoji } from "./bluffPlayerAvatar";
import type { PartyShellPalette } from "../theme/theme";

type Props = {
  roster: BluffRoster;
  title: string;
  emptyHint: string;
  myConnectionId?: string | null;
  /** Shown next to the matching row when `myConnectionId` is set (e.g. guest view). */
  youLabel?: string;
  partyPal: PartyShellPalette;
};

export function BluffPlayersRoster({ roster, title, emptyHint, myConnectionId, youLabel = "", partyPal }: Props) {
  const entries = Object.entries(roster).sort(([a], [b]) => a.localeCompare(b));

  return (
    <Paper
      elevation={0}
      sx={{
        p: 2,
        mb: 2,
        borderRadius: 3,
        border: `1.5px solid ${partyPal.inkFaint}`,
        bgcolor: "rgba(255,255,255,0.04)",
      }}
      aria-live="polite"
    >
      <Typography sx={{ fontWeight: 800, mb: 1.5, color: partyPal.ink }}>{title}</Typography>
      {entries.length === 0 ? (
        <Typography sx={{ fontSize: 14, color: partyPal.inkDim, lineHeight: 1.5 }}>{emptyHint}</Typography>
      ) : (
        <Stack spacing={1.25}>
          {entries.map(([connectionId, displayName]) => (
            <Stack key={connectionId} direction="row" spacing={1.5} alignItems="center">
              <Avatar
                sx={{
                  width: 44,
                  height: 44,
                  bgcolor: "rgba(255,255,255,0.1)",
                  fontSize: "1.35rem",
                  border: `1px solid ${partyPal.inkFaint}`,
                }}
                aria-hidden
              >
                {bluffAvatarEmoji(connectionId)}
              </Avatar>
              <Box sx={{ minWidth: 0, flex: 1 }}>
                <Typography sx={{ fontSize: 16, fontWeight: 700, color: partyPal.ink, lineHeight: 1.3 }} noWrap>
                  {displayName}
                  {youLabel && myConnectionId && connectionId === myConnectionId ? (
                    <Typography component="span" sx={{ fontWeight: 600, color: partyPal.inkDim, ml: 0.75, fontSize: 14 }}>
                      {youLabel}
                    </Typography>
                  ) : null}
                </Typography>
              </Box>
            </Stack>
          ))}
        </Stack>
      )}
    </Paper>
  );
}
